import { CalSlot } from '@/types/scheduling';
import { logger } from '@/utils/logger';

/**
 * Extracts slots recursively from various Cal.com API response structures
 * IMPORTANT: Ignores dates with empty arrays
 */
function extractSlots(obj: any): any[] {
  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return [];
    }
    return obj.flatMap(item => extractSlots(item));
  } else if (obj && typeof obj === 'object' && (obj.time || obj.start || obj.datetime || obj.date)) {
    return [obj];
  } else if (obj && typeof obj === 'object') {
    return Object.entries(obj).flatMap(([key, value]) => {
      if (Array.isArray(value) && value.length === 0) {
        logger.debug(`   🚫 Date ${key} ignorée: array vide`);
        return [];
      }
      return extractSlots(value);
    });
  } else if (typeof obj === 'string') {
    return obj ? [{ start: obj }] : [];
  } else {
    return [];
  }
}

/**
 * Transforms raw Cal.com slot data into standardized CalSlot format
 */
export function transformSlotsToCalSlots(
  slotsData: any[],
  durationMinutes: number
): CalSlot[] {
  return slotsData
    .map((slot: any) => {
      let timeValue = slot.time || slot.start || slot;
      
      if (typeof timeValue === 'object' && timeValue !== null) {
        timeValue = timeValue.time || timeValue.start || timeValue.datetime || timeValue.date;
      }
      
      if (!timeValue) {
        return null;
      }
      
      let startDate: Date;
      try {
        startDate = new Date(timeValue);
        if (isNaN(startDate.getTime())) {
          return null;
        }
      } catch (e) {
        return null;
      }
      
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      
      return {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        available: true,
      };
    })
    .filter((slot): slot is CalSlot => slot !== null);
}

/**
 * Parses Cal.com v2 API response and extracts slots
 * 
 * Cal.com v2 format:
 * {
 *   "status": "success",
 *   "data": {
 *     "2050-09-05": [
 *       { "start": "2050-09-05T09:00:00.000+02:00" }
 *     ]
 *   }
 * }
 */
export function parseCalApiResponse(data: any): any[] {
  let slotsData: any[] = [];
  
  // Handle Cal.com v2 format: data.data is an object with date keys
  if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    // Check if it's the v2 format (date-keyed object)
    const keys = Object.keys(data.data);
    if (keys.length > 0 && keys[0].match(/^\d{4}-\d{2}-\d{2}$/)) {
      // v2 format: extract all slots from date-keyed object
      slotsData = extractSlots(data.data);
    } else if (data.data.slots && typeof data.data.slots === 'object') {
      // Legacy format with nested slots
      slotsData = extractSlots(data.data.slots);
    } else {
      // Try extracting directly
      slotsData = extractSlots(data.data);
    }
  } else if (Array.isArray(data)) {
    slotsData = extractSlots(data);
  } else if (data.slots) {
    slotsData = extractSlots(data.slots);
  }
  
  if (!Array.isArray(slotsData)) {
    slotsData = [];
  }
  
  return slotsData;
}

